import { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, Loader2, Sparkles, Download, RotateCcw, Stars, X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import axios from 'axios';
import html2canvas from 'html2canvas';

import { BACKEND_URL } from '../lib/backend';

const API = `${BACKEND_URL}/api`;

// Fun compatibility messages for Gen Z
const getVibeMessage = (score) => {
  if (score >= 90) return { emoji: "🔥💕", text: "Twin Flame Energy!", subtext: "You two are literally made for each other bestie" };
  if (score >= 80) return { emoji: "✨💖", text: "Main Character Couple!", subtext: "The universe said YES to this match" };
  if (score >= 70) return { emoji: "💫🥰", text: "It's Giving Soulmate!", subtext: "Major green flags detected" };
  if (score >= 60) return { emoji: "💕🌙", text: "Cute Potential!", subtext: "Worth a shot, no cap" };
  if (score >= 50) return { emoji: "🤔💭", text: "Situationship Vibes", subtext: "Could go either way tbh" };
  if (score >= 40) return { emoji: "😬💔", text: "Proceed with Caution", subtext: "Red flags might be lurking" };
  return { emoji: "💀🚩", text: "Not the One Bestie", subtext: "The stars said swipe left" };
};

export const DashboardLoveMatch = () => {
  const [showModal, setShowModal] = useState(false);
  const [name1, setName1] = useState('');
  const [dob1, setDob1] = useState('');
  const [name2, setName2] = useState('');
  const [dob2, setDob2] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const resultRef = useRef(null);

  const checkCompatibility = async () => {
    if (!name1 || !dob1 || !name2 || !dob2) return;
    
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/public/love-match`, {
        name1: name1.trim(),
        dob1: dob1,
        name2: name2.trim(),
        dob2: dob2
      });
      setResult(response.data);
    } catch (error) {
      console.error('Error checking compatibility:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetMatch = () => {
    setName1('');
    setDob1('');
    setName2('');
    setDob2('');
    setResult(null);
  };

  const closeModal = () => {
    setShowModal(false);
    resetMatch();
  };

  const downloadAsImage = async () => {
    if (!resultRef.current || !result) return;
    
    setDownloading(true);
    try {
      const canvas = await html2canvas(resultRef.current, {
        backgroundColor: '#0a0118',
        scale: 2,
        useCORS: true,
        logging: false,
        allowTaint: true,
      });
      
      // Convert to blob for more reliable download
      canvas.toBlob((blob) => {
        if (blob) {
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `love-match-${name1}-${name2}.jpg`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        }
      }, 'image/jpeg', 0.9);
    } catch (error) {
      console.error('Error generating image:', error);
    } finally {
      setTimeout(() => setDownloading(false), 500);
    }
  };

  const vibeMsg = result ? getVibeMessage(result.score) : null;

  return (
    <>
      {/* Card Trigger */}
      <motion.div
        whileHover={{ scale: 1.02 }}
        className="cosmic-card p-6 cursor-pointer hover:border-[#C84B32]/30 transition-all group h-full"
        onClick={() => setShowModal(true)}
        data-testid="quick-love-match-card"
      >
        <div className="flex items-center gap-4 mb-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-pink-500/20 to-purple-500/20 flex items-center justify-center group-hover:from-pink-500/30 group-hover:to-purple-500/30 transition-colors">
            <Heart className="w-6 h-6 text-pink-600" fill="currentColor" />
          </div>
          <div>
            <h3 className="font-['Fraunces'] text-lg text-[#282622] group-hover:text-[#C84B32] transition-colors">Quick Love Match</h3>
            <p className="text-[#6B6560] text-xs">Are you meant to be? Find out!</p>
          </div>
        </div>
        <p className="text-[#8A8279] text-xs">
          Check zodiac compatibility instantly with just names and dates
        </p>
        <p className="mt-3 text-pink-600 text-xs group-hover:text-pink-700">
          Click to check your vibe →
        </p>
      </motion.div>

      {/* Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={closeModal}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="cosmic-card w-full max-w-md p-4 sm:p-6 max-h-[85vh] overflow-y-auto"
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-4 sm:mb-6">
                <div className="text-center flex-1">
                  <h3 className="font-['Fraunces'] text-xl sm:text-2xl text-white">
                    Are You <span className="bg-gradient-to-r from-pink-400 to-purple-400 bg-clip-text text-transparent">Meant to Be?</span>
                  </h3>
                  <p className="text-[#6B6560] text-xs sm:text-sm mt-1">Find out if the stars align for you two ✨</p>
                </div>
                <button onClick={closeModal} className="text-[#6B6560] hover:text-white ml-2 sm:ml-4">
                  <X className="w-5 h-5 sm:w-6 sm:h-6" />
                </button>
              </div>

              <AnimatePresence mode="wait">
                {!result ? (
                  <motion.div
                    key="form"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                  >
                    {/* Person 1 */}
                    <div className="mb-5">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-pink-500 to-rose-500 flex items-center justify-center">
                          <Heart className="w-4 h-4 text-white" fill="white" />
                        </div>
                        <span className="text-white font-medium">You</span>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <Input
                          type="text"
                          placeholder="Your name"
                          value={name1}
                          onChange={(e) => setName1(e.target.value)}
                          data-testid="dashboard-name1-input"
                          className="bg-white border-[#282622]/10 text-white placeholder:text-[#6B6560] focus:border-pink-500"
                        />
                        <Input
                          type="date"
                          value={dob1}
                          onChange={(e) => setDob1(e.target.value)}
                          data-testid="dashboard-dob1-input"
                          className="bg-white border-[#282622]/10 text-white focus:border-pink-500"
                        />
                      </div>
                    </div>

                    {/* Heart Divider */}
                    <div className="flex items-center justify-center my-4">
                      <div className="flex-1 h-px bg-gradient-to-r from-transparent via-pink-500/30 to-transparent"></div>
                      <motion.div
                        animate={{ scale: [1, 1.2, 1] }}
                        transition={{ duration: 1.5, repeat: Infinity }}
                        className="mx-4"
                      >
                        <Heart className="w-6 h-6 text-pink-500" fill="currentColor" />
                      </motion.div>
                      <div className="flex-1 h-px bg-gradient-to-r from-transparent via-pink-500/30 to-transparent"></div>
                    </div>

                    {/* Person 2 */}
                    <div className="mb-6">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-indigo-500 flex items-center justify-center">
                          <Heart className="w-4 h-4 text-white" fill="white" />
                        </div>
                        <span className="text-white font-medium">Your Crush</span>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <Input
                          type="text"
                          placeholder="Their name"
                          value={name2}
                          onChange={(e) => setName2(e.target.value)}
                          data-testid="dashboard-name2-input"
                          className="bg-white border-[#282622]/10 text-white placeholder:text-[#6B6560] focus:border-purple-500"
                        />
                        <Input
                          type="date"
                          value={dob2}
                          onChange={(e) => setDob2(e.target.value)}
                          data-testid="dashboard-dob2-input"
                          className="bg-white border-[#282622]/10 text-white focus:border-purple-500"
                        />
                      </div>
                    </div>

                    <Button
                      onClick={checkCompatibility}
                      disabled={!name1 || !dob1 || !name2 || !dob2 || loading}
                      data-testid="dashboard-check-vibe-btn"
                      className="w-full bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white py-6 rounded-xl text-lg font-semibold disabled:opacity-50"
                    >
                      {loading ? (
                        <Loader2 className="w-5 h-5 animate-spin mr-2" />
                      ) : (
                        <Sparkles className="w-5 h-5 mr-2" />
                      )}
                      {loading ? 'Reading the stars...' : 'Check Our Vibe ✨'}
                    </Button>
                  </motion.div>
                ) : (
                  <motion.div
                    key="result"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center"
                  >
                    {/* Downloadable Result Card */}
                    <div ref={resultRef} className="bg-white p-4 sm:p-6 rounded-xl">
                      {/* Nakshatra Branding */}
                      <div className="flex items-center justify-center gap-2 mb-3">
                        <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-gradient-to-br from-[#C84B32] to-[#F5B942] flex items-center justify-center">
                          <Sparkles className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-white" />
                        </div>
                        <span className="font-['Fraunces'] text-base sm:text-lg text-[#C84B32]">Nakshatra</span>
                      </div>
                      
                      {/* Names */}
                      <div className="flex items-center justify-center gap-3 sm:gap-4 mb-3 sm:mb-5">
                        <div className="text-center">
                          <div className="w-11 h-11 sm:w-14 sm:h-14 rounded-full bg-gradient-to-br from-pink-500 to-rose-500 flex items-center justify-center text-lg sm:text-xl font-bold text-white mb-1 sm:mb-2">
                            {name1.charAt(0).toUpperCase()}
                          </div>
                          <p className="text-white font-medium truncate max-w-[60px] sm:max-w-[70px] text-xs sm:text-sm">{name1}</p>
                          <p className="text-pink-600 text-[10px] sm:text-xs">{result.sign1}</p>
                        </div>
                        
                        <Heart className="w-6 h-6 sm:w-8 sm:h-8 text-pink-500" fill="currentColor" />
                        
                        <div className="text-center">
                          <div className="w-11 h-11 sm:w-14 sm:h-14 rounded-full bg-gradient-to-br from-purple-500 to-indigo-500 flex items-center justify-center text-lg sm:text-xl font-bold text-white mb-1 sm:mb-2">
                            {name2.charAt(0).toUpperCase()}
                          </div>
                          <p className="text-white font-medium truncate max-w-[60px] sm:max-w-[70px] text-xs sm:text-sm">{name2}</p>
                          <p className="text-purple-600 text-[10px] sm:text-xs">{result.sign2}</p>
                        </div>
                      </div>

                      {/* Score Circle - Smaller on mobile */}
                      <div className="relative w-20 h-20 sm:w-28 sm:h-28 mx-auto mb-3 sm:mb-4">
                        <svg className="w-full h-full transform -rotate-90" viewBox="0 0 112 112">
                          <circle
                            cx="56"
                            cy="56"
                            r="48"
                            fill="none"
                            stroke="rgba(255,255,255,0.1)"
                            strokeWidth="8"
                          />
                          <circle
                            cx="56"
                            cy="56"
                            r="48"
                            fill="none"
                            stroke="#EC4899"
                            strokeWidth="8"
                            strokeLinecap="round"
                            strokeDasharray={`${(result.score / 100) * 301} 301`}
                          />
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-xl sm:text-2xl font-bold text-pink-600">
                            {result.score}%
                          </span>
                        </div>
                      </div>

                      {/* Vibe Message */}
                      {vibeMsg && (
                        <div className="mb-3 sm:mb-4">
                          <div className="text-xl sm:text-2xl mb-1">{vibeMsg.emoji}</div>
                          <h3 className="text-base sm:text-lg font-bold text-pink-600 mb-0.5 sm:mb-1">
                            {vibeMsg.text}
                          </h3>
                          <p className="text-[#6B6560] text-[10px] sm:text-xs">{vibeMsg.subtext}</p>
                        </div>
                      )}

                      {/* Cosmic Analysis */}
                      <div className="bg-[#FCE9D9]/40 rounded-lg p-2 sm:p-3 text-left">
                        <div className="flex items-center gap-2 mb-1 sm:mb-2">
                          <Stars className="w-3 h-3 text-[#C84B32]" />
                          <span className="text-[#C84B32] text-[10px] sm:text-xs font-medium">Cosmic Tea</span>
                        </div>
                        <p className="text-[#282622] text-[10px] sm:text-xs leading-relaxed line-clamp-4 sm:line-clamp-none">{result.analysis}</p>
                      </div>
                      
                      {/* Footer */}
                      <p className="text-[#8A8279] text-[10px] sm:text-xs mt-2 sm:mt-3">nakshatra.app</p>
                    </div>

                    {/* Action Buttons (outside downloadable area) */}
                    <div className="flex gap-2 sm:gap-3 mt-4 sm:mt-5">
                      <Button
                        onClick={resetMatch}
                        variant="outline"
                        className="flex-1 border-[#282622]/15 text-white hover:bg-[#FCE9D9]/60 text-sm py-2 sm:py-3"
                        data-testid="try-again-btn"
                      >
                        <RotateCcw className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                        Try Again
                      </Button>
                      <Button
                        onClick={downloadAsImage}
                        disabled={downloading}
                        className="flex-1 bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-sm py-2 sm:py-3"
                        data-testid="download-result-btn"
                      >
                        {downloading ? (
                          <Loader2 className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2 animate-spin" />
                        ) : (
                          <Download className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                        )}
                        {downloading ? 'Saving...' : 'Download'}
                      </Button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default DashboardLoveMatch;
